import React from 'react';
import { Ruler, ArrowDownUp, Move, Circle } from 'lucide-react';

/**
 * Компонент для ввода размеров помещения в зависимости от выбранной формы
 */
const RoomDimensionsSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  measurementUnits
}) => {
  const unit = measurementUnits.find(u => u.id === formData.measurementUnit)?.acronym || 'м';
  
  return (
    <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Ruler className="w-5 h-5 mr-2 text-fuchsia-500" />
        Размеры помещения
      </h3>
      
      {/* Прямоугольное помещение */}
      {formData.roomShape === 'rectangle' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="length" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Длина ({unit})
            </label>
            <input
              type="text"
              id="length"
              name="length"
              value={formData.length}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.length ? 'input-error' : ''}`}
              placeholder={`Длина в ${unit}`}
            />
            {errors.length && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.length}
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="width" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ширина ({unit})
            </label>
            <input
              type="text"
              id="width"
              name="width"
              value={formData.width}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.width ? 'input-error' : ''}`}
              placeholder={`Ширина в ${unit}`}
            />
            {errors.width && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.width}
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Г-образное помещение */}
      {formData.roomShape === 'l-shaped' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 p-3 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800 mb-2">
            <p className="text-sm text-fuchsia-700 dark:text-fuchsia-300 flex items-center gap-2">
              <ArrowDownUp className="w-4 h-4" />
              Основная часть помещения
            </p>
          </div>
          
          <div>
            <label htmlFor="mainLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Длина основной части ({unit})
            </label>
            <input
              type="text"
              id="mainLength"
              name="mainLength"
              value={formData.mainLength}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.mainLength ? 'input-error' : ''}`}
              placeholder={`Длина в ${unit}`}
            />
            {errors.mainLength && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.mainLength}
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="mainWidth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ширина основной части ({unit})
            </label>
            <input
              type="text"
              id="mainWidth"
              name="mainWidth"
              value={formData.mainWidth}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.mainWidth ? 'input-error' : ''}`}
              placeholder={`Ширина в ${unit}`}
            />
            {errors.mainWidth && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.mainWidth}
              </p>
            )}
          </div>
          
          <div className="sm:col-span-2 p-3 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800 mb-2 mt-2">
            <p className="text-sm text-fuchsia-700 dark:text-fuchsia-300 flex items-center gap-2">
              <ArrowDownUp className="w-4 h-4" />
              Выступающая часть помещения
            </p>
          </div>
          
          <div>
            <label htmlFor="extensionLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Длина выступа ({unit})
            </label>
            <input
              type="text"
              id="extensionLength"
              name="extensionLength"
              value={formData.extensionLength}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.extensionLength ? 'input-error' : ''}`}
              placeholder={`Длина в ${unit}`}
            />
            {errors.extensionLength && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.extensionLength}
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="extensionWidth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ширина выступа ({unit})
            </label>
            <input
              type="text"
              id="extensionWidth"
              name="extensionWidth"
              value={formData.extensionWidth}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.extensionWidth ? 'input-error' : ''}`}
              placeholder={`Ширина в ${unit}`}
            />
            {errors.extensionWidth && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.extensionWidth}
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Комната со скошенным углом */}
      {formData.roomShape === 'angled' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 p-3 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800 mb-2">
            <p className="text-sm text-fuchsia-700 dark:text-fuchsia-300 flex items-center gap-2">
            <ArrowDownUp className="w-4 h-4" />
              Основной прямоугольник
            </p>
          </div>
          
          <div>
            <label htmlFor="baseLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Длина основания ({unit})
            </label>
            <input
              type="text"
              id="baseLength"
              name="baseLength"
              value={formData.baseLength}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.baseLength ? 'input-error' : ''}`}
              placeholder={`Длина в ${unit}`}
            />
            {errors.baseLength && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.baseLength}
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="baseWidth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ширина основания ({unit})
            </label>
            <input
              type="text"
              id="baseWidth"
              name="baseWidth"
              value={formData.baseWidth}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.baseWidth ? 'input-error' : ''}`}
              placeholder={`Ширина в ${unit}`}
            />
            {errors.baseWidth && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.baseWidth}
              </p>
            )}
          </div>
          
          <div className="sm:col-span-2 p-3 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800 mb-2 mt-2">
            <p className="text-sm text-fuchsia-700 dark:text-fuchsia-300 flex items-center gap-2">
            <ArrowDownUp className="w-4 h-4" />
              Размеры скошенного угла
            </p>
          </div>
          
          <div>
            <label htmlFor="cutoutLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Длина скоса ({unit})
            </label>
            <input
              type="text"
              id="cutoutLength"
              name="cutoutLength"
              value={formData.cutoutLength}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.cutoutLength ? 'input-error' : ''}`}
              placeholder={`Длина в ${unit}`}
            />
            {errors.cutoutLength && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.cutoutLength}
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="cutoutWidth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ширина скоса ({unit})
            </label>
            <input
              type="text"
              id="cutoutWidth"
              name="cutoutWidth"
              value={formData.cutoutWidth}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.cutoutWidth ? 'input-error' : ''}`}
              placeholder={`Ширина в ${unit}`}
            />
            {errors.cutoutWidth && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.cutoutWidth}
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Комната с эркером */}
      {formData.roomShape === 'bay-window' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 p-3 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800 mb-2">
            <p className="text-sm text-fuchsia-700 dark:text-fuchsia-300 flex items-center gap-2">
            <ArrowDownUp className="w-4 h-4" />
              Основная часть комнаты
            </p>
          </div>
          
          <div>
            <label htmlFor="roomLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Длина комнаты ({unit})
            </label>
            <input
              type="text"
              id="roomLength"
              name="roomLength"
              value={formData.roomLength}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.roomLength ? 'input-error' : ''}`}
              placeholder={`Длина в ${unit}`}
            />
            {errors.roomLength && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.roomLength}
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="roomWidth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ширина комнаты ({unit})
            </label>
            <input
              type="text"
              id="roomWidth"
              name="roomWidth"
              value={formData.roomWidth}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.roomWidth ? 'input-error' : ''}`}
              placeholder={`Ширина в ${unit}`}
            />
            {errors.roomWidth && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.roomWidth}
              </p>
            )}
          </div>
          
          <div className="sm:col-span-2 p-3 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800 mb-2 mt-2">
            <p className="text-sm text-fuchsia-700 dark:text-fuchsia-300 flex items-center gap-2">
            <ArrowDownUp className="w-4 h-4" />
              Эркер
            </p>
          </div>
          
          <div>
            <label htmlFor="bayWindowDepth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Глубина эркера ({unit})
            </label>
            <input
              type="text"
              id="bayWindowDepth"
              name="bayWindowDepth"
              value={formData.bayWindowDepth}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.bayWindowDepth ? 'input-error' : ''}`}
              placeholder={`Глубина в ${unit}`}
            />
            {errors.bayWindowDepth && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.bayWindowDepth}
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="bayWindowWidth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ширина эркера ({unit})
            </label>
            <input
              type="text"
              id="bayWindowWidth"
              name="bayWindowWidth"
              value={formData.bayWindowWidth}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.bayWindowWidth ? 'input-error' : ''}`}
              placeholder={`Ширина в ${unit}`}
            />
            {errors.bayWindowWidth && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.bayWindowWidth}
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Комната с нишей */}
      {formData.roomShape === 'niche' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 p-3 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800 mb-2">
            <p className="text-sm text-fuchsia-700 dark:text-fuchsia-300 flex items-center gap-2">
            <ArrowDownUp className="w-4 h-4" />
              Основная часть комнаты
            </p>
          </div>
          
          <div>
            <label htmlFor="roomLengthWithNiche" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Длина комнаты ({unit})
            </label>
            <input
              type="text"
              id="roomLengthWithNiche"
              name="roomLengthWithNiche"
              value={formData.roomLengthWithNiche}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.roomLengthWithNiche ? 'input-error' : ''}`}
              placeholder={`Длина в ${unit}`}
            />
            {errors.roomLengthWithNiche && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.roomLengthWithNiche}
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="roomWidthWithNiche" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ширина комнаты ({unit})
            </label>
            <input
              type="text"
              id="roomWidthWithNiche"
              name="roomWidthWithNiche"
              value={formData.roomWidthWithNiche}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.roomWidthWithNiche ? 'input-error' : ''}`}
              placeholder={`Ширина в ${unit}`}
            />
            {errors.roomWidthWithNiche && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.roomWidthWithNiche}
              </p>
            )}
          </div>
          
          <div className="sm:col-span-2 p-3 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800 mb-2 mt-2">
            <p className="text-sm text-fuchsia-700 dark:text-fuchsia-300 flex items-center gap-2">
            <ArrowDownUp className="w-4 h-4" />
              Ниша
            </p>
          </div>
          
          <div>
            <label htmlFor="nicheDepth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Глубина ниши ({unit})
            </label>
            <input
              type="text"
              id="nicheDepth"
              name="nicheDepth"
              value={formData.nicheDepth}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.nicheDepth ? 'input-error' : ''}`}
              placeholder={`Глубина в ${unit}`}
            />
            {errors.nicheDepth && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.nicheDepth}
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="nicheWidth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ширина ниши ({unit})
            </label>
            <input
              type="text"
              id="nicheWidth"
              name="nicheWidth"
              value={formData.nicheWidth}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.nicheWidth ? 'input-error' : ''}`}
              placeholder={`Ширина в ${unit}`}
            />
            {errors.nicheWidth && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.nicheWidth}
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Комната с колонной */}
      {formData.roomShape === 'column' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 p-3 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800 mb-2">
            <p className="text-sm text-fuchsia-700 dark:text-fuchsia-300 flex items-center gap-2">
            <ArrowDownUp className="w-4 h-4" />
              Основная часть комнаты
            </p>
          </div>
          
          <div>
            <label htmlFor="roomLengthWithColumn" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Длина комнаты ({unit})
            </label>
            <input
              type="text"
              id="roomLengthWithColumn"
              name="roomLengthWithColumn"
              value={formData.roomLengthWithColumn}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.roomLengthWithColumn ? 'input-error' : ''}`}
              placeholder={`Длина в ${unit}`}
            />
            {errors.roomLengthWithColumn && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.roomLengthWithColumn}
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="roomWidthWithColumn" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ширина комнаты ({unit})
            </label>
            <input
              type="text"
              id="roomWidthWithColumn"
              name="roomWidthWithColumn"
              value={formData.roomWidthWithColumn}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.roomWidthWithColumn ? 'input-error' : ''}`}
              placeholder={`Ширина в ${unit}`}
            />
            {errors.roomWidthWithColumn && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.roomWidthWithColumn}
              </p>
            )}
          </div>
          
          <div className="sm:col-span-2 p-3 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800 mb-2 mt-2">
            <p className="text-sm text-fuchsia-700 dark:text-fuchsia-300 flex items-center gap-2">
            <ArrowDownUp className="w-4 h-4" />
              Колонна
            </p>
          </div>
          
          <div>
            <label htmlFor="columnSide" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Сторона колонны ({unit})
            </label>
            <input
              type="text"
              id="columnSide"
              name="columnSide"
              value={formData.columnSide}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.columnSide ? 'input-error' : ''}`}
              placeholder={`Сторона в ${unit}`}
            />
            {errors.columnSide && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.columnSide}
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Круглая комната */}
      {formData.roomShape === 'circle' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="diameter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Диаметр ({unit})
            </label>
            <input
              type="text"
              id="diameter"
              name="diameter"
              value={formData.diameter}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.diameter ? 'input-error' : ''}`}
              placeholder={`Диаметр в ${unit}`}
            />
            {errors.diameter && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.diameter}
              </p>
            )}
          </div>
          
          <div className="flex items-center justify-center">
            <div className="h-20 w-20 rounded-full border-2 border-fuchsia-500 flex items-center justify-center">
              <Circle className="w-10 h-10 text-fuchsia-500" />
            </div>
          </div>
        </div>
      )}
      
      {/* Произвольный многоугольник */}
      {formData.roomShape === 'polygon' && (
        <div className="sm:col-span-2">
          <div className="p-3 bg-fuchsia-50 dark:bg-fuchsia-900/20 rounded-lg border border-fuchsia-100 dark:border-fuchsia-800 mb-4">
            <p className="text-sm text-fuchsia-700 dark:text-fuchsia-300 flex items-center gap-2">
              <Move className="w-4 h-4" />
              В режиме произвольной формы используйте редактор для задания многоугольника.
              В демо-версии калькулятора для упрощения эта функция реализуется как простой прямоугольник.
            </p>
          </div>
        </div>
      )}
      
      {/* Высота потолка (для всех типов комнат) */}
      <div className="mt-6 pt-5 border-t border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="ceilingHeight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Высота потолка ({unit})
            </label>
            <input
              type="text"
              id="ceilingHeight"
              name="ceilingHeight"
              value={formData.ceilingHeight}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.ceilingHeight ? 'input-error' : ''}`}
              placeholder={`Высота в ${unit}`}
            />
            {errors.ceilingHeight && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.ceilingHeight}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomDimensionsSection;